/**
 * usePfRevampAnalytics — thin dispatcher over the existing analytics plumbing.
 *
 * Call site: `const track = usePfRevampAnalytics(); track({ type: '...', payload: ... })`.
 *
 * Each dispatched event:
 *   1. Fires on the revamp-namespaced event (`pf_revamp.*`) to all standard
 *      destinations via `logAnalyticsEvent`.
 *   2. Fires any mapped legacy events (from `legacyEventsFor`) under the
 *      legacy event names — this preserves the Firebase funnel continuity
 *      required by §11 during the canary.
 *   3. For `ORDER_PLACED`, also forwards to Userpilot (the legacy
 *      `ORDER_PURCHASED` event was tracked on Userpilot directly, not via
 *      `logAnalyticsEvent`).
 *
 * The hook returns a stable callback so consumers can safely include it in
 * `useCallback` dep arrays.
 *
 * Error isolation: the underlying destinations swallow their own errors
 * (see analytics/firebase.ts etc.), so this dispatcher has no try/catch.
 */

import { useCallback, useRef } from 'react'
import { Userpilot } from 'userpilot'

import { logAnalyticsEvent } from 'utils/analytics'
import { logger } from 'utils/logging'

import type { PfRevampEvent } from './events'
import { PfRevampEventName } from './events'
import { LEGACY_ORDER_PURCHASED, legacyEventsFor, legacyPayloadFor } from './legacyEvents'

/** Debounce window for draft_saved. Plan §6 caps auto-save events to 1/30s. */
const DRAFT_SAVED_MIN_INTERVAL_MS = 30_000

export interface PfRevampAnalyticsDispatcher {
  (event: PfRevampEvent): void
}

/**
 * Exported for direct (non-hook) call sites — e.g. the auto-save subscription
 * runs outside a component. All the same fan-out + legacy parity logic, but
 * you own the debounce state (see `makeDraftSavedGate`).
 */
/**
 * Which purchase flow an event came from.
 *
 * v2 adopts the LEGACY event names, so the two flows land in one series and can
 * be compared on a single chart; this attribute is what tells them apart.
 * **Legacy is deliberately not changed** — it sends no `version` at all, so the
 * analytics side reads legacy as "version is not set". Note that is ABSENT, not
 * a literal null: a `!= 2` filter does not match an unset property in every
 * tool, so segments must be built on "is not set".
 *
 * Stamped here rather than at the emit sites: a funnel with a hole in it is
 * worse than no funnel, and there is no call site that can forget it.
 *
 * `logAnalyticsEvent` ALSO marks events while the flow is mounted
 * (`utils/analytics/pfFlowVersion`), which is what covers the shared components
 * the flow hosts. The two do not fight: that helper leaves a body that already
 * names a version alone, and this is the explicit contract for the flow's own
 * events — it holds even for a dispatch from outside a mounted shell, such as a
 * test or the auto-save subscription.
 */
export const PF_FLOW_VERSION = 2

const logPfEvent = (name: string, body: Record<string, unknown>): void => {
  logAnalyticsEvent(name, { ...body, version: PF_FLOW_VERSION })
}

export const dispatchPfRevampEvent = (event: PfRevampEvent): void => {
  // 1. Primary revamp-namespaced event (Firebase / GA / Mixpanel / Intercom).
  logPfEvent(event.type, event.payload as unknown as Record<string, unknown>)

  // 2. Legacy parity events.
  const legacyNames = legacyEventsFor(event)
  for (const legacyName of legacyNames) {
    logPfEvent(legacyName, legacyPayloadFor(event, legacyName))
  }

  // 3. Userpilot parity for order_placed (legacy fired Userpilot directly).
  if (event.type === PfRevampEventName.ORDER_PLACED) {
    try {
      Userpilot.track(LEGACY_ORDER_PURCHASED)
    } catch (err) {
      logger.warn('pf-revamp analytics: Userpilot.track failed', err, {
        context: { component: 'usePfRevampAnalytics', metadata: { event: event.type } },
      })
    }
  }
}

/**
 * Gate factory for draft_saved. Returns a function that fires at most once
 * every `DRAFT_SAVED_MIN_INTERVAL_MS` per call site instance. We deliberately
 * do NOT use `lodash.debounce` here — we want leading-edge + hard cooldown,
 * no trailing fire, so rapid consecutive saves within a session count as one.
 */
export const makeDraftSavedGate = () => {
  let lastFiredAt = 0
  return (event: PfRevampEvent): boolean => {
    if (event.type !== PfRevampEventName.DRAFT_SAVED) return true
    const now = Date.now()
    if (now - lastFiredAt < DRAFT_SAVED_MIN_INTERVAL_MS) return false
    lastFiredAt = now
    return true
  }
}

/**
 * Primary React hook. Memoised dispatcher; internally holds a stable
 * draft-saved gate across renders so auto-save churn doesn't spam Firebase.
 */
export const usePfRevampAnalytics = (): PfRevampAnalyticsDispatcher => {
  const draftSavedGateRef = useRef(makeDraftSavedGate())

  return useCallback((event: PfRevampEvent) => {
    if (!draftSavedGateRef.current(event)) return
    dispatchPfRevampEvent(event)
  }, [])
}
